import { useEffect, useState } from 'react';
import { IonContent, IonPage } from '@ionic/react';
import { useHistory, Redirect } from 'react-router-dom';
import { useAuth } from '@/lib/auth/useAuth';
import { updateProfile, profileInitials, stringToColor } from '@/lib/api/profiles';
import { Logo } from '@/components/ui/Logo';

export default function EditProfilePage() {
  const { status, profile, user } = useAuth();
  const history = useHistory();
  const [displayName, setDisplayName] = useState('');
  const [bio, setBio] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (profile) {
      setDisplayName(profile.display_name ?? '');
      setBio(profile.bio ?? '');
    }
  }, [profile]);

  if (status === 'unauthenticated') return <Redirect to="/auth" />;
  if (status === 'needs-handle') return <Redirect to="/auth/claim-handle" />;

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await updateProfile({
        display_name: displayName.trim() || null,
        bio: bio.trim() || null,
      });
      history.push(`/@${profile!.handle}`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <IonPage>
      <IonContent scrollY fullscreen>
        <header className="sticky top-0 z-10 border-b border-gray-200 bg-white">
          <div className="mx-auto flex max-w-2xl items-center gap-3 px-5 py-3">
            <button
              onClick={() => history.goBack()}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
              aria-label="Back"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 18l-6-6 6-6"/>
              </svg>
            </button>
            <Logo size="sm" />
            <span className="text-sm font-semibold text-gray-700">Edit profile</span>
          </div>
        </header>

        <main className="mx-auto max-w-2xl px-5 py-10">
          {profile && (
            <form onSubmit={save} className="flex flex-col gap-6">
              {/* Avatar preview */}
              <div className="flex items-center gap-4">
                <div
                  className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full text-xl font-bold text-white"
                  style={{ background: stringToColor(user?.id ?? '') }}
                >
                  {profileInitials({ ...profile, display_name: displayName || null })}
                </div>
                <div>
                  <p className="font-semibold text-gray-900">@{profile.handle}</p>
                  <p className="text-xs text-gray-400">Handle cannot be changed</p>
                </div>
              </div>

              {/* Display name */}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-gray-700" htmlFor="display-name">
                  Display name
                </label>
                <input
                  id="display-name"
                  type="text"
                  value={displayName}
                  onChange={e => setDisplayName(e.target.value)}
                  maxLength={80}
                  placeholder="Your name"
                  className="rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-[#1a56a0] focus:ring-1 focus:ring-[#1a56a0]"
                />
                <p className="text-xs text-gray-400">{displayName.length}/80</p>
              </div>

              {/* Bio */}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-gray-700" htmlFor="bio">
                  Bio
                </label>
                <textarea
                  id="bio"
                  value={bio}
                  onChange={e => setBio(e.target.value)}
                  maxLength={500}
                  rows={4}
                  placeholder="A short bio about yourself"
                  className="resize-y rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm leading-relaxed outline-none transition-colors focus:border-[#1a56a0] focus:ring-1 focus:ring-[#1a56a0]"
                />
                <p className="text-xs text-gray-400">{bio.length}/500</p>
              </div>

              {error && (
                <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => history.goBack()}
                  className="rounded-lg border border-gray-200 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-lg bg-[#2f3e46] px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#1e2c33] disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </form>
          )}
        </main>
      </IonContent>
    </IonPage>
  );
}
